/**
 * FilterRepository
 *
 * Centralized configuration for all search filter fields.
 * Drives UI rendering across all strategies (Desktop, Mobile, Tablet).
 *
 * SOLID Compliance:
 * - SRP: Only stores filter configuration
 * - OCP: Extensible (add new filters without code changes)
 * - LSP: N/A (data structure, not inheritance)
 * - ISP: N/A (data structure, not interface)
 * - DIP: Components depend on this abstraction
 *
 * Usage:
 * import FilterRepository, { getPrimaryFilters, getAdvancedFilters } from './FilterRepository';
 * const primaryFilters = getPrimaryFilters();
 *
 * Location: apps/web/src/config/FilterRepository.js
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System
 */

import LocationAutocomplete from '../components/search/LocationAutocomplete';
import SearchDateRangePicker from '../components/calendar/SearchDateRangePicker';
import GuestCountSelector from '../components/calendar/GuestCountSelector';
import CategoryRefinementAccordion from '../components/search/CategoryRefinementAccordion';
import TagSelector from '../components/search/TagSelector';
import ActivityTypeSelector from '../components/search/ActivityTypeSelector';

// TODO: Create these components (Phase 2 - Week 2)
// import PriceRangeSlider from '../components/search/filters/PriceRangeSlider';
// import RatingSelector from '../components/search/filters/RatingSelector';

const FilterRepository = {
  /**
   * Location filter - Primary filter (highest priority)
   * Required for all searches
   */
  location: {
    id: 'location',
    label: 'Location',
    type: 'autocomplete',
    placeholder: 'Anywhere',
    component: LocationAutocomplete,
    urlParam: 'location',
    priority: 100, // Highest priority
    displayInMobile: true,
    displayInDesktop: true,
    requiredForSearch: true,
    icon: '📍',
    validation: {
      minLength: 2,
      required: true,
      errorMessage: 'Please enter a location (minimum 2 characters)'
    },
    helpText: 'Search for guides near a city or landmark'
  },

  /**
   * Date range filter - Primary filter
   * Optional for searches
   */
  dates: {
    id: 'dates',
    label: 'Date',
    type: 'date_range',
    placeholder: 'Any week',
    component: SearchDateRangePicker,
    urlParams: ['startDate', 'endDate'], // Multiple URL parameters
    priority: 80,
    displayInMobile: true,
    displayInDesktop: true,
    requiredForSearch: false,
    icon: '📅',
    validation: {
      maxRangeDays: 90,
      errorMessage: 'Date range cannot exceed 90 days'
    },
    helpText: 'Select your adventure dates'
  },

  /**
   * Guest count filter - Primary filter
   * Defaults to 1 guest
   */
  guests: {
    id: 'guests',
    label: 'Guests',
    type: 'number',
    placeholder: '1 guest',
    component: GuestCountSelector,
    urlParam: 'partySize',
    priority: 60,
    displayInMobile: true,
    displayInDesktop: true,
    defaultValue: 1,
    icon: '👥',
    validation: {
      min: 1,
      max: 20,
      errorMessage: 'Guest count must be between 1 and 20'
    },
    helpText: 'Number of people in your group'
  },

  /**
   * Activity type filter - DEPRECATED
   * Replaced by categories filter (MVP 1.3 Phase 2)
   * Kept for backward compatibility
   */
  activityType: {
    id: 'activityType',
    label: 'Adventure Type',
    type: 'multi_select',
    placeholder: 'Any type',
    component: ActivityTypeSelector,
    urlParam: 'activitySlug',
    priority: 40,
    displayInMobile: true,
    displayInDesktop: true,
    deprecated: true, // Use categories filter instead
    icon: '🏔️',
    validation: {
      maxSelections: 5,
      errorMessage: 'You can select up to 5 activity types'
    },
    helpText: 'Choose the type of outdoor adventure'
  },

  /**
   * Category filter - Primary filter (MVP 1.3 Phase 2)
   * Hierarchical category taxonomy (L1 → L2 → L3)
   * Replaces activityType with full taxonomy
   */
  categories: {
    id: 'categories',
    label: 'Categories',
    type: 'hierarchical_multi_select',
    placeholder: 'Any category',
    component: CategoryRefinementAccordion,
    urlParams: ['categoryL1', 'categoryL2', 'categoryL3'], // Multiple URL parameters
    priority: 45, // Higher than old activityType
    displayInMobile: true,
    displayInDesktop: true,
    icon: '🏔️',
    validation: {
      maxSelections: 10,
      errorMessage: 'You can select up to 10 categories'
    },
    helpText: 'Browse by activity categories and service types'
  },

  /**
   * Tag filter - Primary filter (MVP 1.3 Phase 2)
   * Multi-select tags grouped by category
   * Environment, difficulty, duration, features, etc.
   */
  tags: {
    id: 'tags',
    label: 'Tags',
    type: 'multi_select',
    placeholder: 'Any tags',
    component: TagSelector,
    urlParam: 'tags', // Comma-separated tag IDs
    priority: 35,
    displayInMobile: true,
    displayInDesktop: true,
    icon: '🏷️',
    validation: {
      maxSelections: 15,
      errorMessage: 'You can select up to 15 tags'
    },
    helpText: 'Filter by tags (environment, difficulty, duration, etc.)'
  },

  /**
   * Price range filter - Advanced filter (desktop drawer only)
   * TODO: Create PriceRangeSlider component
   */
  priceRange: {
    id: 'priceRange',
    label: 'Price Range',
    type: 'range',
    placeholder: 'Any price',
    component: null, // TODO: PriceRangeSlider (Phase 2, Week 2)
    urlParams: ['minPrice', 'maxPrice'],
    priority: 20,
    displayInMobile: false, // Desktop drawer only
    displayInDesktop: true,
    isAdvanced: true, // Shown in "More filters" drawer
    icon: '💰',
    validation: {
      min: 0,
      max: 5000,
      errorMessage: 'Price must be between $0 and $5,000'
    },
    helpText: 'Set your budget for the adventure'
  },

  /**
   * Rating filter - Advanced filter (desktop drawer only)
   * TODO: Create RatingSelector component
   */
  rating: {
    id: 'rating',
    label: 'Minimum Rating',
    type: 'star_select',
    placeholder: 'Any rating',
    component: null, // TODO: RatingSelector (Phase 2, Week 2)
    urlParam: 'minRating',
    priority: 10,
    displayInMobile: false, // Desktop drawer only
    displayInDesktop: true,
    isAdvanced: true, // Shown in "More filters" drawer
    icon: '⭐',
    validation: {
      min: 1,
      max: 5,
      errorMessage: 'Rating must be between 1 and 5 stars'
    },
    helpText: 'Filter by guide rating'
  }
};

/**
 * Get all filters
 * @returns {Object} - All filter configurations
 */
export function getAllFilters() {
  return FilterRepository;
}

/**
 * Get primary filters (mobile + desktop inline)
 * @returns {Array<Object>} - Array of primary filter configs
 */
export function getPrimaryFilters() {
  return Object.values(FilterRepository)
    .filter(f => !f.isAdvanced)
    .sort((a, b) => b.priority - a.priority); // Sort by priority (highest first)
}

/**
 * Get advanced filters (desktop drawer only)
 * @returns {Array<Object>} - Array of advanced filter configs
 */
export function getAdvancedFilters() {
  return Object.values(FilterRepository)
    .filter(f => f.isAdvanced)
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Get filter by ID
 * @param {string} id - Filter ID
 * @returns {Object|null} - Filter config or null if not found
 */
export function getFilterById(id) {
  return FilterRepository[id] || null;
}

/**
 * Get filters for mobile display
 * @returns {Array<Object>} - Array of filters visible on mobile
 */
export function getMobileFilters() {
  return Object.values(FilterRepository)
    .filter(f => f.displayInMobile)
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Get filters for desktop display
 * @returns {Array<Object>} - Array of filters visible on desktop
 */
export function getDesktopFilters() {
  return Object.values(FilterRepository)
    .filter(f => f.displayInDesktop)
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Get required filters (must be filled for search)
 * @returns {Array<Object>} - Array of required filter configs
 */
export function getRequiredFilters() {
  return Object.values(FilterRepository)
    .filter(f => f.requiredForSearch)
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Validate filter value
 * @param {string} filterId - Filter ID
 * @param {any} value - Filter value to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export function validateFilterValue(filterId, value) {
  const filter = getFilterById(filterId);

  if (!filter) {
    return { valid: false, error: `Unknown filter: ${filterId}` };
  }

  const validation = filter.validation;

  // Check required
  if (validation.required && (!value || value === '')) {
    return { valid: false, error: validation.errorMessage || 'This field is required' };
  }

  // Check min length (for strings)
  if (validation.minLength && typeof value === 'string' && value.length < validation.minLength) {
    return { valid: false, error: validation.errorMessage || `Minimum length is ${validation.minLength}` };
  }

  // Check min value (for numbers)
  if (validation.min !== undefined && typeof value === 'number' && value < validation.min) {
    return { valid: false, error: validation.errorMessage || `Minimum value is ${validation.min}` };
  }

  // Check max value (for numbers)
  if (validation.max !== undefined && typeof value === 'number' && value > validation.max) {
    return { valid: false, error: validation.errorMessage || `Maximum value is ${validation.max}` };
  }

  // Check max selections (for multi-select)
  if (validation.maxSelections && Array.isArray(value) && value.length > validation.maxSelections) {
    return { valid: false, error: validation.errorMessage || `Maximum ${validation.maxSelections} selections allowed` };
  }

  // Check date range (for dates filter)
  if (filterId === 'dates' && validation.maxRangeDays) {
    const { startDate, endDate } = value || {};
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      if (diffDays > validation.maxRangeDays) {
        return { valid: false, error: validation.errorMessage };
      }
    }
  }

  return { valid: true, error: null };
}

/**
 * Get filter display label for pills
 * @param {string} filterId - Filter ID
 * @param {any} value - Filter value
 * @returns {string} - Display label with icon
 */
export function getFilterDisplayLabel(filterId, value) {
  const filter = getFilterById(filterId);

  if (!filter) {
    return String(value);
  }

  const icon = filter.icon || '';

  switch (filterId) {
    case 'location':
      return `${icon} ${value}`;

    case 'dates':
      if (value.startDate && value.endDate) {
        const start = new Date(value.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const end = new Date(value.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${icon} ${start} - ${end}`;
      }
      return `${icon} ${new Date(value.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'guests':
      return `${icon} ${value} guest${value > 1 ? 's' : ''}`;

    case 'activityType':
      return `${icon} ${value}`;

    case 'categories':
      if (Array.isArray(value)) {
        if (value.length === 0) {return filter.placeholder;}
        if (value.length === 1) {return `${icon} ${value[0].name}`;}
        return `${icon} ${value.length} categories`;
      }
      return `${icon} ${value.name || value}`;

    case 'tags':
      if (Array.isArray(value)) {
        if (value.length === 0) {return filter.placeholder;}
        if (value.length === 1) {return `🏷️ ${value[0].name}`;}
        return `🏷️ ${value.length} tags`;
      }
      return `🏷️ ${value.name || value}`;

    case 'priceRange': {
      const minPrice = value.minPrice || 0;
      const maxPrice = value.maxPrice || '∞';
      return `${icon} $${minPrice} - ${maxPrice === '∞' ? maxPrice : '$' + maxPrice}`;
    }

    case 'rating':
      return `${'⭐'.repeat(value)}+`;

    default:
      return `${icon} ${value}`;
  }
}

export default FilterRepository;
